import { checkPhoneNumber, updateUser } from '@/api/user';
import { Button, StyledForm, Text } from '@/components/common';
import { PhoneField } from '@/components/form';
import { sendVerificationCode } from '@/libs/firebase';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import userReducer from '@/redux/reducers/userReducer';
import { ModalHeader } from '@/styles/common';
import { ERROR_CODES } from '@/utils/constant';
import { ModalRenderer } from '@/utils/modalUtils';
import { Box } from '@mui/material';
import { Formik, FormikProps } from 'formik';
import parsePhoneNumberFromString from 'libphonenumber-js';
import { get } from 'lodash';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import VerificationPhoneCodeModal from '../VerificationPhoneCode';

const VerifyPhoneModal: React.FC<any> = ({ phoneNumber, closeModal, forceLogin }) => {
  const { t } = useTranslation();
  const formikRef = useRef<FormikProps<{ phoneNumber: string }>>(null);
  const dispatch = useAppDispatch();
  const userId = useAppSelector((state: any) => get(state, 'user.userInfo.id'));
  const userInfo = useAppSelector((state: any) => state.user.userInfo);
  const phoneNum: string | undefined = parsePhoneNumberFromString(phoneNumber)?.nationalNumber?.toString();
  const countryCode = parsePhoneNumberFromString(phoneNumber)?.countryCallingCode?.toString();
  const [phoneCode, setPhoneCode] = useState(countryCode ?? '44');
  const onClose = () => {
    closeModal();
  };
  const onSubmit = async (values: any) => {
    const phoneNumber = '+' + phoneCode + values.phoneNumber;
    // clearRecapchaContent();
    try {
      dispatch({ type: 'SHOW_LOADING' });
      if (userInfo?.phone_number !== phoneCode + values.phoneNumber) {
        await checkPhoneNumber(phoneNumber);
      }
      const user = await updateUser(userId, { phone_number: phoneNumber });
      dispatch(userReducer.actions.setUserInfo(user));
      const isSendVerificationSuccess = await sendVerificationCode(phoneNumber);
      if (isSendVerificationSuccess) {
        ModalRenderer.open({
          component: VerificationPhoneCodeModal,
          theme: 'white',
          inMobile: 'bottom',
          props: {
            phoneNumber: phoneNumber,
            forceLogin,
          },
        });
      }
    } catch (error) {
      if (error === ERROR_CODES.INVALID_PHONE_NUMBER || error === ERROR_CODES.EXISTING_PHONE_NUMBER) {
        formikRef.current?.setFieldError('phoneNumber', t('ERROR_MESSAGE_INVALID_PHONE_NUMBER'));
        return;
      }
      if (error === ERROR_CODES.TOO_MANY_REQUESTS || error === ERROR_CODES.TOO_MANY_REQUEST_CODE) {
        formikRef.current?.setFieldError('phoneNumber', t('ERROR_MESSAGE_TOO_MANY_REQUESTS'));
        return;
      }
      if (error === ERROR_CODES.TOO_MANY_ATTEMPS_CODE) {
        formikRef.current?.setFieldError('phoneNumber', t('ERROR_MESSAGE_TOO_MANY_ATTEMPS_CODE'));
        return;
      }
      formikRef.current?.setFieldError('phoneNumber', t('ERROR_MESSAGE_SOMETHING_WENT_WRONG'));
    } finally {
      dispatch({ type: 'HIDE_LOADING' });
    }
  };
  return (
    <>
      <ModalHeader color="primary" variant="title" size="26" sx={{ lineHeight: '28px' }}>
        <img
          style={{ width: '32px', height: '32px', cursor: 'pointer' }}
          src="/images/icon/icon_back.svg"
          onClick={onClose}
          alt=""
        />
        {t('VERIFY_YOUR_NUMBER')}
      </ModalHeader>
      <Text color="primary" variant="content" size="16" style={{ marginBottom: '24px' }}>
        {t('VERIFY_YOUR_NUMBER_CONTENT')}
      </Text>
      <Formik initialValues={{ phoneNumber: phoneNum || '' }} onSubmit={onSubmit} innerRef={formikRef}>
        {({ errors, values, isValid }) => (
          <StyledForm>
            <PhoneField
              type="tel"
              name="phoneNumber"
              onHandlePhoneCode={setPhoneCode}
              PhoneCode={phoneCode}
              config={{
                themeColor: 'white',
                variant: 'standard',
                label: t('PHONE_NUMBER'),
                InputProps: {
                  'data-testid': 'test_phone',
                },
              }}
            />
            <Box sx={{ mt: 5 }}>
              <Button
                fullWidth
                variant="contained"
                color="dark"
                type="submit"
                disabled={!!Boolean(errors.phoneNumber) || !values.phoneNumber}
                data-testid="test_btn_continue"
              >
                {t('CONTINUE')}
              </Button>
            </Box>
          </StyledForm>
        )}
      </Formik>
    </>
  );
};

export default VerifyPhoneModal;
